import React from 'react';

import { Box, Grid } from '@mui/material';
import GenericLoadingText from '@apigrowfinance/pbolt-generic-loading-text';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import { IAccountHistoryItem } from '@clients/accountHistoryClient';
import AccountHistoryList from '@components/accountHistory/accountHistory';
import AccountHistoryBlockSkeleton from '@components/accountHistory/skeletons/AccountHistoryBlockSkeleton';
import LoadingError from '@components/loadingError/LoadingError';
import { useOperatorSession } from '@contexts/OperatorSeesionProvider';
import useGetAccountHistory from '@hooks/queries/useGetAccountHistory';

const AccountHistory = () => {
    const { accountId, customerId } = useParams();
    const { protocol } = useOperatorSession();
    let history: IAccountHistoryItem[] | [] = [];

    const { data, isError, isLoading, fetchNextPage, hasNextPage } =
        useGetAccountHistory({ accountId, customerId });

    const flattenPages = data?.pages?.flat(1);

    if (flattenPages) {
        history = flattenPages.reduce((previousValue, currentValue) => {
            if (currentValue.items === null) return previousValue;

            // Remove the currently running session from the history list
            const newHistoryItems = currentValue.items?.filter(
                (historyItem) => historyItem.protocol !== protocol,
            );

            return [...previousValue, ...newHistoryItems];
        }, []);
    }

    if (isError) {
        return (
            <Box sx={{ mt: 10 }}>
                <LoadingError />
            </Box>
        );
    }

    return (
        <Grid container>
            {isLoading && <AccountHistoryBlockSkeleton />}
            {!isLoading && (
                <Grid item xs={12}>
                    <InfiniteScroll
                        dataLength={history?.length}
                        next={fetchNextPage}
                        hasMore={hasNextPage}
                        loader={
                            <GenericLoadingText
                                displayText={
                                    <FormattedMessage
                                        id="AccountHistory.Loading"
                                        defaultMessage="Loading..."
                                    />
                                }
                                showLoader
                            />
                        }>
                        <AccountHistoryList items={history} />
                    </InfiniteScroll>
                </Grid>
            )}
        </Grid>
    );
};

export default AccountHistory;
